import {useState} from 'react';

import {
	Bank,
	FinancialDataType,
	ModalityCodeEnum,
	ProfessionalDataType,
	RequestData,
} from 'domain/usecases';
import {makePatchRefundUpdateRequest, makePostRefundCreateRequest} from 'main/factories/usecases';
import {makePutRefundUpdateRequest} from 'main/factories/usecases/private/refund/request/put-update-request';
import {FeaturesEnum, OperationStateTypesEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {useGetBeneficiaryBankDataQuery} from '../../profile/queries/useGetBeneficiaryBankDataQuery';
import {useUploadedRefundFilesStore} from '../components/UploadRefundFiles/store/useUploadedRefundFilesStore';
import {IFile} from '../components/UploadRefundFiles/types';
import {
	FiscalDocumentSchema,
	PatientDataSchema,
	ProviderDataSchema,
} from '../pages/Request/components/ReceiptRegistration/validations';
import {DEFAULT_REQUEST_FORM} from '../pages/Request/constants';
import {useRefundProvidersMutation} from '../pages/Request/mutations/useGetRefundProvidersMutation';
import {useAccordionExpandedStore} from '../pages/Request/store/useAccordionExpandedStore';
import {useFinancialDataStore} from '../pages/Request/store/useFinancialDataStore';
import {useListRequestFormsStore} from '../pages/Request/store/useListRequestFormsStore';
import {useProfessionalDataStore} from '../pages/Request/store/useProfessionalDataStore';
import {useRequestDataStore} from '../pages/Request/store/useRequestDataStore';
import {useSelectedRequestIndexStore} from '../pages/Request/store/useSelectedRequestIndexStore';
import {useSubmitStatusStore} from '../pages/Request/store/useSubmitStatusStore';
import {useTreatmentDataStore} from '../pages/Request/store/useTreatmentDataStore';
import {CustomTreatmentDataType, SubmitStatusEnum} from '../pages/Request/types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

export const useRefundRequest = () => {
	const [operationState, setOperationState] = useState(OperationStateTypesEnum.NONE);
	const treatmentData = useTreatmentDataStore(state => state.data);
	const setTreatmentData = useTreatmentDataStore(state => state.setData);
	const professionalData = useProfessionalDataStore(state => state.data);
	const setProfessionalData = useProfessionalDataStore(state => state.setData);
	const financialData = useFinancialDataStore(state => state.data);
	const setFinancialData = useFinancialDataStore(state => state.setData);
	const {data: bankDataState} = useGetBeneficiaryBankDataQuery();

	const listRequestForms = useListRequestFormsStore(state => state.list);
	const setListRequestForms = useListRequestFormsStore(state => state.setList);

	const selectedRequestIndex = useSelectedRequestIndexStore(state => state.index);
	const setSelectedRequestIndex = useSelectedRequestIndexStore(state => state.setIndex);
	const nextSelectedRequestIndex = useSelectedRequestIndexStore(state => state.nextIndex);
	const previousSelectedRequestIndex = useSelectedRequestIndexStore(state => state.previousIndex);

	const submitStatus = useSubmitStatusStore(state => state.status);
	const isAddNewRequestInProtocol = submitStatus === SubmitStatusEnum.ADD_NEW_REQUEST_IN_PROTOCOL;
	const isUpdateRequest = submitStatus === SubmitStatusEnum.UPDATE_REQUEST;

	const {currentEmail, currentPhone} = usePersonalData();

	const requests = useRequestDataStore(state => state.data);
	const setRequests = useRequestDataStore(state => state.setData);

	const resetRequests = useRequestDataStore(state => state.resetData);
	const resetSubmitStatus = useSubmitStatusStore(state => state.reset);
	const resetAccordion = useAccordionExpandedStore(state => state.resetAccordion);
	const resetListRequestForms = useListRequestFormsStore(state => state.resetList);
	const resetSelectedRequestIndex = useSelectedRequestIndexStore(state => state.resetIndex);
	const resetTreatmentData = useTreatmentDataStore(state => state.resetData);
	const resetProfessionalData = useProfessionalDataStore(state => state.resetData);
	const resetFinancialData = useFinancialDataStore(state => state.resetData);
	const resetUploads = useUploadedRefundFilesStore(state => state.resetUploadedFiles);
	const {mutateAsync: getProfessional} = useRefundProvidersMutation({
		onMutate: () => {
			setOperationState(OperationStateTypesEnum.SUBMITTING);
		},
		onSettled: () => {
			setOperationState(OperationStateTypesEnum.PENDING);
		},
	});

	const IS_LOADING = operationState === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR = operationState === OperationStateTypesEnum.ERROR;

	const handleResetStates = () => {
		const modality = {
			modality: treatmentData.modality,
			modalityCode: treatmentData.modalityCode,
		};
		resetAccordion();
		resetTreatmentData();
		resetProfessionalData();
		resetFinancialData();
		resetUploads();
		setTreatmentData({
			...treatmentData,
			modality: modality.modality,
			modalityCode: modality.modalityCode,
		});
	};

	const handleUpdateProviderDataForm = (provider: Partial<ProviderDataSchema>) => {
		setListRequestForms(
			listRequestForms.map((currRequest, index) =>
				index === selectedRequestIndex ? {...currRequest, provider} : currRequest,
			),
		);
	};

	const handleUpdatePatientDataForm = (patient: Partial<PatientDataSchema>) => {
		setListRequestForms(
			listRequestForms.map((currRequest, index) =>
				index === selectedRequestIndex ? {...currRequest, patient} : currRequest,
			),
		);
	};

	const handleUpdateFiscalDocumentDataForm = (fiscal: Partial<FiscalDocumentSchema>) => {
		setListRequestForms(
			listRequestForms.map((currRequest, index) =>
				index === selectedRequestIndex ? {...currRequest, fiscal} : currRequest,
			),
		);
	};

	const handleUpdateUploads = (uploads: IFile[]) => {
		setListRequestForms(
			listRequestForms.map((currRequest, index) =>
				index === selectedRequestIndex ? {...currRequest, uploads} : currRequest,
			),
		);
	};

	const handleCreateNewRequestInProtocol = () => {
		const newList = [...listRequestForms, {...DEFAULT_REQUEST_FORM}];
		setListRequestForms(newList);
		nextSelectedRequestIndex();
	};

	const handleDeleteLastRequest = () => {
		const newList = [...listRequestForms];
		newList.pop();
		setListRequestForms(newList);
		previousSelectedRequestIndex();
	};

	const handleUpdateTreatmentData = (data: CustomTreatmentDataType) => {
		setTreatmentData(data);
	};

	const handleUpdateProfessionalData = (data: ProfessionalDataType) => {
		setProfessionalData(data);
	};

	const handleUpdateFinancialData = (data: FinancialDataType) => {
		setFinancialData(data);
	};

	async function handleCreateRequest(financial: FinancialDataType, bankData: Bank) {
		setOperationState(OperationStateTypesEnum.SUBMITTING);

		try {
			const response = await makePostRefundCreateRequest().post({
				requestData: {
					protocolData: {
						email: currentEmail,
						phoneNumber: currentPhone,
					},
					bankData: {
						agency: bankData.agency,
						agencyDigit: bankData.agencyDigit,
						account: bankData.account,
						accountDigit: bankData.accountDigit,
						bankCode: bankData.bank.code,
						bankName: bankData.bank.description,
					},
					financialData: financial,
					professionalData,
					treatmentData: {
						beneficiary: treatmentData.beneficiary,
						date: treatmentData.date,
						modality: treatmentData.modality,
						modalityCode: treatmentData.modalityCode,
						planCode: treatmentData.planCode,
						specialty: treatmentData.specialty,
					},
				},
			});

			setRequests({
				protocol: response.content.protocol,
				protocolCode: response.content.protocolCode,
				bankData: {
					agency: bankData.agency,
					agencyDigit: bankData.agencyDigit,
					account: bankData.account,
					accountDigit: bankData.accountDigit,
					bankCode: bankData.bank.code,
					bankName: bankData.bank.description,
				},
				requestData: [
					{
						beneficiaryName:
							treatmentData.fullBeneficiary?.fullSocialName ||
							treatmentData.fullBeneficiary?.fullName,
						request: response.content.request,
						requestCode: response.content.requestCode,
						financialData: financial,
						professionalData,
						treatmentData,
					},
				],
			});

			return response.content;
		} catch (e) {
			setOperationState(OperationStateTypesEnum.ERROR);
			return null;
		}
	}

	async function handleUpdateRequest({
		financial,
		bankData,
		professional,
		treatment,
	}: Partial<{
		financial: RequestData['financialData'];
		professional: RequestData['professionalData'];
		bankData: Bank;
		treatment: CustomTreatmentDataType;
	}>) {
		setOperationState(OperationStateTypesEnum.SUBMITTING);
		const requestCode = requests.requestData[selectedRequestIndex]?.requestCode;
		if (typeof requestCode !== 'number') {
			setOperationState(OperationStateTypesEnum.ERROR);
			return null;
		}

		const currentBankData = bankData ?? bankDataState;
		const currentFinancialData = financial ?? financialData;
		const currentProfessionalData = professional ?? professionalData;
		const currentTreatmentData = treatment ?? treatmentData;

		try {
			const response = await makePutRefundUpdateRequest().post({
				requestData: {
					protocolData: {
						email: currentEmail,
						phoneNumber: currentPhone,
					},
					bankData: {
						...currentBankData!,
						bankCode: currentBankData!.bank.code,
						bankName: currentBankData!.bank.description,
					},
					financialData: currentFinancialData,
					professionalData: currentProfessionalData,
					treatmentData: {
						beneficiary: currentTreatmentData.beneficiary,
						date: currentTreatmentData.date,
						modality: currentTreatmentData.modality,
						modalityCode: currentTreatmentData.modalityCode,
						planCode: currentTreatmentData.planCode,
						specialty: currentTreatmentData.specialty,
					},
				},
				protocol: requests.protocol,
				protocolCode: requests.protocolCode,
				requestCode,
			});

			setRequests({
				protocol: response.content.protocol,
				protocolCode: response.content.protocolCode,
				bankData: {
					agency: currentBankData!.agency,
					agencyDigit: currentBankData?.agencyDigit,
					account: currentBankData!.account,
					accountDigit: currentBankData?.accountDigit,
					bankCode: currentBankData!.bank.code,
					bankName: currentBankData!.bank.description,
				},
				requestData: requests.requestData.map(data =>
					data.requestCode === response.content.requestCode
						? {
								beneficiaryName:
									currentTreatmentData.fullBeneficiary?.fullSocialName ||
									currentTreatmentData.fullBeneficiary?.fullName,
								request: response.content.request,
								requestCode: response.content.requestCode,
								financialData: currentFinancialData,
								professionalData: currentProfessionalData,
								treatmentData: currentTreatmentData,
							}
						: data,
				),
			});

			return response.content;
		} catch (e) {
			setOperationState(OperationStateTypesEnum.ERROR);
			return null;
		}
	}

	async function handleAddNewRequestInProtocol(financial: FinancialDataType, bankData: Bank) {
		setOperationState(OperationStateTypesEnum.SUBMITTING);
		const creatorProtocolUserCode = requests.requestData[0].treatmentData.beneficiary.code;

		try {
			const response = await makePatchRefundUpdateRequest(requests.protocol).patch({
				requestData: {
					treatmentData: {
						beneficiary: treatmentData.beneficiary,
						date: treatmentData.date,
						modality: treatmentData.modality,
						modalityCode: treatmentData.modalityCode,
						planCode: treatmentData.planCode,
						specialty: treatmentData.specialty,
					},
					bankData: {
						agency: bankData.agency,
						agencyDigit: bankData.agencyDigit,
						account: bankData.account,
						accountDigit: bankData.accountDigit,
						bankCode: bankData.bank.code,
						bankName: bankData.bank.description,
					},
					professionalData,
					financialData: financial,
				},
				refundData: {
					protocolCode: requests.protocolCode,
					userCode: creatorProtocolUserCode,
				},
			});

			setRequests({
				...requests,
				requestData: [
					...requests.requestData,
					{
						beneficiaryName:
							treatmentData.fullBeneficiary?.fullSocialName ||
							treatmentData.fullBeneficiary?.fullName,
						request: response.content.request,
						requestCode: response.content.requestCode,
						financialData: financial,
						professionalData,
						treatmentData,
					},
				],
			});

			return response.content;
		} catch (e) {
			setOperationState(OperationStateTypesEnum.ERROR);
			return null;
		}
	}

	const handleUpdateAtoms = {
		treatmentData: handleUpdateTreatmentData,
		professionalData: handleUpdateProfessionalData,
		financialData: handleUpdateFinancialData,
	};

	const atoms = {
		treatmentData,
		professionalData,
		financialData,
	};

	const handleUpdateFormAtoms = {
		providerData: handleUpdateProviderDataForm,
		patientData: handleUpdatePatientDataForm,
		fiscalDocumentData: handleUpdateFiscalDocumentDataForm,
		uploads: handleUpdateUploads,
	};

	const formAtoms = {
		providerDataForm: listRequestForms[selectedRequestIndex]?.provider,
		patientDataForm: listRequestForms[selectedRequestIndex]?.patient,
		fiscalDocumentDataForm: listRequestForms[selectedRequestIndex]?.fiscal,
		uploads: listRequestForms[selectedRequestIndex]?.uploads,
	};

	const showAdditionalRequestFlow =
		isAddNewRequestInProtocol ||
		(isUpdateRequest && formAtoms.uploads && formAtoms.uploads?.length >= 1);

	const {isEnabled: isRefundTherapiesEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_THERAPIES,
	);

	const isTherapiesFlow =
		atoms?.treatmentData?.modalityCode?.toString() === ModalityCodeEnum.THERAPIES;

	const isTherapiesFlowEnabled = isRefundTherapiesEnabled && isTherapiesFlow;

	return {
		setOperationState,
		IS_LOADING,
		IS_ERROR,
		getProfessional,
		atoms,
		handleUpdateAtoms,
		formAtoms,
		handleUpdateFormAtoms,
		handleCreateRequest,
		handleUpdateRequest,
		requests,
		handleResetStates,
		handleAddNewRequestInProtocol,
		handleDeleteLastRequest,
		resetRequests,
		setRequests,
		isAddNewRequestInProtocol,
		isUpdateRequest,
		handleCreateNewRequestInProtocol,
		selectedRequestIndex,
		setSelectedRequestIndex,
		listRequestForms,
		showAdditionalRequestFlow,
		resetListRequestForms,
		resetSelectedRequestIndex,
		resetSubmitStatus,
		isTherapiesFlowEnabled,
	};
};
